import { debugLog } from '@shared/utils/Logger';
import { LPCSpriteRenderer } from './LPCSpriteRenderer';
import { getAllIconPaths, registerCustomIcon, CUSTOM_ICONS_PATH } from '@client/ui/ItemIcons';

/** Union type for drawable image sources (works with ctx.drawImage) */
export type DrawableImage = HTMLImageElement | HTMLCanvasElement;

export interface LoadedAssets {
  images: Map<string, HTMLImageElement>;
  audio: Map<string, HTMLAudioElement>;
}

export class AssetLoader {
  private images: Map<string, HTMLImageElement> = new Map();
  /** Canvas-based images (procedural icons, etc.) */
  private canvasImages: Map<string, HTMLCanvasElement> = new Map();
  private audio: Map<string, HTMLAudioElement> = new Map();
  private loadingProgress: number = 0;
  private totalAssets: number = 0;
  private loadedAssets: number = 0;

  async loadInitialAssets(): Promise<void> {
    debugLog('Assets', 'Loading initial assets...');

    // Load LPC sprite layers
    await LPCSpriteRenderer.preloadLayers(this);

    // Load item icons (mapped + custom folder)
    await this.loadItemIcons();

    debugLog('Assets', 'Initial assets loaded');
  }

  private async loadItemIcons(): Promise<void> {
    // 1. Try loading custom icons from sprites/items/icons/ via manifest
    await this.loadCustomItemIcons();

    // 2. Load mapped icons (7soul/db32)
    const iconPaths = getAllIconPaths();
    debugLog('Assets', `Loading ${iconPaths.length} item icons...`);

    const assets = iconPaths.map(path => ({
      key: `item_icon:${path}`,
      src: path,
    }));

    await this.loadImages(assets).catch(err => {
      debugLog('Assets', `Some item icons failed to load: ${err}`);
    });

    debugLog('Assets', 'Item icons loaded');
  }

  /**
   * Try to load custom icons from public/sprites/items/icons/.
   * Reads manifest.json from that folder — if it exists, loads listed icons.
   * Manifest format: ["weapon_sword_1", "chest_ar_5", ...]  (baseId list)
   */
  private async loadCustomItemIcons(): Promise<void> {
    try {
      const resp = await fetch(`${CUSTOM_ICONS_PATH}/manifest.json`);
      if (!resp.ok) return;
      const baseIds: string[] = await resp.json();
      if (!Array.isArray(baseIds) || baseIds.length === 0) return;

      debugLog('Assets', `Loading ${baseIds.length} custom item icons...`);

      let loaded = 0;
      for (const baseId of baseIds) {
        const path = `${CUSTOM_ICONS_PATH}/${baseId}.png`;
        try {
          await this.loadImage(`item_icon:${path}`, path);
          registerCustomIcon(baseId);
          loaded++;
        } catch {
          // Custom icon file missing — skip silently
        }
      }

      if (loaded > 0) {
        debugLog('Assets', `Loaded ${loaded} custom item icons`);
      }
    } catch {
      // No manifest or folder — skip custom loading
    }
  }

  async loadImage(key: string, src: string): Promise<HTMLImageElement> {
    // Check if already loaded
    const existing = this.images.get(key);
    if (existing) return existing;

    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        this.images.set(key, img);
        this.loadedAssets++;
        this.updateProgress();
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`));
      };

      img.src = src;
    });
  }

  async loadImages(assets: { key: string; src: string }[]): Promise<HTMLImageElement[]> {
    this.totalAssets += assets.length;

    const promises = assets.map(({ key, src }) => this.loadImage(key, src));
    return Promise.all(promises);
  }

  async loadAudio(key: string, src: string): Promise<HTMLAudioElement> {
    // Check if already loaded
    const existing = this.audio.get(key);
    if (existing) return existing;

    return new Promise((resolve, reject) => {
      const audio = new Audio();

      audio.oncanplaythrough = () => {
        this.audio.set(key, audio);
        this.loadedAssets++;
        this.updateProgress();
        resolve(audio);
      };

      audio.onerror = () => {
        reject(new Error(`Failed to load audio: ${src}`));
      };

      audio.src = src;
      audio.load();
    });
  }

  /**
   * Get a loaded image (HTMLImageElement or HTMLCanvasElement).
   * Checks real images first, then canvas-based fallbacks.
   */
  getImage(key: string): DrawableImage | undefined {
    return this.images.get(key) || this.canvasImages.get(key);
  }

  /** Store a canvas-based image (e.g. procedural icon) in the cache. */
  setCanvasImage(key: string, canvas: HTMLCanvasElement): void {
    this.canvasImages.set(key, canvas);
  }

  getAudio(key: string): HTMLAudioElement | undefined {
    return this.audio.get(key);
  }

  private updateProgress(): void {
    if (this.totalAssets > 0) {
      this.loadingProgress = this.loadedAssets / this.totalAssets;
    }
  }

  getProgress(): number {
    return this.loadingProgress;
  }

  /**
   * Unload image assets that are not in the provided set of needed keys.
   * Persistent assets (LPC sprite layers, item icons) are always retained.
   */
  unloadUnused(neededKeys?: Set<string>): void {
    const keysToRemove: string[] = [];
    for (const key of this.images.keys()) {
      // Always keep LPC sprite layers and item icons — they are shared across all zones
      if (key.startsWith('lpc:') || key.startsWith('item_icon:')) continue;
      if (neededKeys && neededKeys.has(key)) continue;
      keysToRemove.push(key);
    }
    for (const key of keysToRemove) {
      this.images.delete(key);
    }
    if (keysToRemove.length > 0) {
      debugLog('Assets', `Unloaded ${keysToRemove.length} unused image assets`);
    }
  }

  // Preload a sprite sheet and return frame data
  async loadSpriteSheet(
    key: string,
    src: string,
    frameWidth: number,
    frameHeight: number
  ): Promise<{
    image: HTMLImageElement;
    frameWidth: number;
    frameHeight: number;
    framesPerRow: number;
    totalFrames: number;
  }> {
    const image = await this.loadImage(key, src);
    const framesPerRow = Math.floor(image.width / frameWidth);
    const rows = Math.floor(image.height / frameHeight);
    const totalFrames = framesPerRow * rows;

    return {
      image,
      frameWidth,
      frameHeight,
      framesPerRow,
      totalFrames,
    };
  }
}
